// src/layouts/HeaderFooterLayout.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import lumosyeLogoLight from '../assets/Lumosye-logo.png';
import lumosyeLogoDark from '../assets/Lumosye-logo-dark.png';

export default function HeaderFooterLayout({ children }) {
  const [scrollY, setScrollY] = useState(0);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [headerVisible, setHeaderVisible] = useState(true);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    if (saved !== null) return JSON.parse(saved);
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // 🌓 Apply & persist dark mode globally
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', darkMode);
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  // 🎢 Scroll visibility effect
  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      setHeaderVisible(currentY < lastScrollY || currentY < 80);
      setScrollY(currentY);
      setLastScrollY(currentY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const headerOpacity = scrollY < 300 ? 1 - scrollY / 600 : 0.45;
  const headerBlur = scrollY < 300 ? 10 + scrollY / 60 : 14;
  const lumosyeLogo = darkMode ? lumosyeLogoDark : lumosyeLogoLight;

  return (
    <div className="min-h-screen flex flex-col bg-[#f9fafb] dark:bg-black text-gray-800 dark:text-gray-200 transition-colors duration-300">
      {/* Frosted Header */}
      <header
        className={`fixed top-0 w-full z-50 transition-all duration-[600ms] ease-[cubic-bezier(0.22,0.61,0.36,1)] ${
          headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-5'
        }`}
        style={{
          backgroundColor: darkMode
            ? `rgba(24,24,27,${headerOpacity * 0.95})`
            : `rgba(255,255,255,${headerOpacity * 0.9})`,
          backdropFilter: `blur(${headerBlur}px)`,
          boxShadow: scrollY > 40 ? '0 1px 8px rgba(0,0,0,0.05)' : 'none',
        }}
      >
        <nav className="container mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-3">
            <img
              src={lumosyeLogo}
              alt="Lumosye Logo"
              className="h-12 md:h-14 w-auto object-contain transition-transform duration-300 hover:scale-[1.03]"
            />
          </Link>

          {/* Right-side nav + Dark toggle */}
          <div className="flex items-center space-x-8 text-sm font-medium text-gray-700 dark:text-gray-200">
            <Link to="/" className="hover:text-blue-600 dark:hover:text-blue-400 transition">
              Home
            </Link>
            <Link to="/privacy" className="hover:text-blue-600 dark:hover:text-blue-400 transition">
              Privacy
            </Link>
            <Link to="/terms" className="hover:text-blue-600 dark:hover:text-blue-400 transition">
              Terms
            </Link>
            <Link to="/support" className="hover:text-blue-600 dark:hover:text-blue-400 transition">
              Support
            </Link>

            {/* 🌗 Global Dark Mode Toggle */}
            <button
              onClick={() => setDarkMode(!darkMode)}
              className="ml-4 text-sm bg-slate-100 dark:bg-zinc-800 text-gray-700 dark:text-gray-100 px-3 py-1 rounded-full hover:scale-105 transition"
              title="Toggle dark mode"
            >
              {darkMode ? '☀️ Light' : '🌙 Dark'}
            </button>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main className="flex-grow pt-28 md:pt-32">{children}</main>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 text-center border-t border-zinc-800">
        <p className="font-medium text-gray-300">
          © {new Date().getFullYear()} Lumosye Health Services, LLC
        </p>
        <div className="mt-4 space-x-8 text-sm">
          <Link to="/privacy" className="hover:text-white transition">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-white transition">Terms of Use</Link>
          <Link to="/support" className="hover:text-white transition">Support</Link>
        </div>
      </footer>
    </div>
  );
}
